import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTimeIT } from "@/lib/formatters";
import { PageHeader } from "../components/PageHeader";
import { Link } from "react-router-dom";
import { toBusinessDateKey } from "@/lib/agendaView";

const DEFAULT_FILTERS = {
  category: "",
  search: "",
  performed_by_role: "",
  after: "",
  before: "",
  limit: "20",
};

const roleOptions = ["owner", "staff", "system", "customer"];

export const ACTIVITY_CATEGORIES = [
  { key: "appointments", label: "Appuntamenti" },
  { key: "customers", label: "Clienti" },
  { key: "services", label: "Servizi" },
  { key: "staff", label: "Staff" },
  { key: "hours", label: "Orari" },
  { key: "reminders", label: "Reminder" },
  { key: "recoveries", label: "Recuperi" },
  { key: "system", label: "Sistema" },
];

const ACTION_LABELS = {
  user_registered: "Utente registrato",
  appointment_created: "Appuntamento creato",
  appointment_rescheduled: "Appuntamento spostato",
  appointment_completed: "Appuntamento completato",
  appointment_noshow: "No-show",
  cancellation_detected: "Cancellazione rilevata",
  cancellation_manual: "Cancellazione manuale",
  cancellation_via_reminder: "Cancellazione da promemoria",
  reminder_confirmed: "Promemoria confermato",
  recovery_confirmed: "Recupero confermato",
  recovery_error: "Errore recupero",
  recovery_attempt: "Tentativo recupero",
  recovery_no_match: "Nessun recupero trovato",
  notification_sent: "Proposta recovery preparata",
  customer_created: "Cliente creato",
  customer_updated: "Cliente aggiornato",
  customer_trashed: "Cliente nel cestino",
  customer_restored: "Cliente ripristinato",
  staff_updated: "Staff aggiornato",
  staff_reactivated: "Staff riattivato",
  staff_trashed: "Staff nel cestino",
  staff_restored: "Staff ripristinato",
  business_hours_updated: "Orari aggiornati",
  service_created: "Servizio creato",
  service_updated: "Servizio aggiornato",
  service_deactivated: "Servizio disattivato",
  service_reactivated: "Servizio riattivato",
  sample_data_created: "Dati di test creati",
  sample_scenario_created: "Scenario di test creato",
};

const MESSAGE_LABELS = {
  "Appointment created.": "Appuntamento creato.",
  "Appointment rescheduled.": "Appuntamento riprogrammato.",
  "Appointment completed.": "Appuntamento completato.",
  "Appointment marked as no-show.": "Appuntamento segnato come no-show.",
  "Appointment cancelled manually by merchant.": "Appuntamento cancellato manualmente dal titolare.",
  "Appointment cancelled via reminder flow.": "Appuntamento cancellato dal flusso promemoria.",
  "Customer created.": "Cliente creato.",
  "Customer moved to trash.": "Cliente spostato nel cestino.",
  "Customer restored from trash.": "Cliente ripristinato dal cestino.",
  "Staff updated.": "Staff aggiornato.",
  "Staff reactivated.": "Staff riattivato.",
  "Staff moved to trash.": "Staff spostato nel cestino.",
  "Staff restored from trash.": "Staff ripristinato dal cestino.",
  "Service created.": "Servizio creato.",
  "Service updated.": "Servizio aggiornato.",
  "Service deactivated.": "Servizio disattivato.",
  "Service reactivated.": "Servizio riattivato.",
  "Business hours updated.": "Orari aggiornati.",
  "Reminder confirmed by customer.": "Promemoria confermato dal cliente.",
  "Recovery candidate confirmed.": "Candidato recovery confermato.",
  "Recovery candidate notification prepared.": "Proposta recovery preparata.",
  "No compatible single replacement found.": "Nessun sostituto compatibile trovato.",
  "Recovery attempt failed.": "Tentativo di recovery fallito.",
  "Owner demo registrato": "Owner demo registrato.",
  "Appuntamenti demo creati": "Appuntamenti demo creati.",
  "Recovery attempts demo creati": "Tentativi di recovery demo creati.",
};

const ACTOR_ROLE_LABELS = {
  owner: "Titolare",
  staff: "Staff",
  system: "Sistema",
  customer: "Cliente",
};

const ACTOR_BADGE_CLASSES = {
  owner: "border-slate-300 bg-slate-50 text-slate-700",
  staff: "border-blue-200 bg-blue-50 text-blue-700",
  system: "border-emerald-200 bg-emerald-50 text-emerald-700",
  customer: "border-amber-200 bg-amber-50 text-amber-700",
  missing: "border-slate-200 bg-white text-slate-500",
};

const SENSITIVE_METADATA_PATTERNS = ["token", "secret", "password", "hash", "raw"];
const TECHNICAL_ID_MIN_LENGTH = 12;

const APPOINTMENT_ACTIONS = new Set([
  "appointment_created",
  "appointment_rescheduled",
  "appointment_completed",
  "appointment_noshow",
  "cancellation_manual",
  "cancellation_via_reminder",
]);

const CUSTOMER_ACTIONS = new Set([
  "customer_created",
  "customer_trashed",
  "customer_restored",
]);

const STAFF_ACTIONS = new Set([
  "staff_trashed",
  "staff_restored",
]);

const BUSINESS_SERVICE_ACTIONS = new Set([
  "business_hours_updated",
  "service_created",
  "service_updated",
  "service_deactivated",
  "service_reactivated",
]);

const RECOVERY_ACTIONS = new Set([
  "reminder_confirmed",
  "notification_sent",
  "recovery_attempt",
  "recovery_confirmed",
  "recovery_no_match",
  "recovery_error",
]);

const METADATA_PREVIEW_FIELDS = {
  appointment: [
    { key: "customer_name", label: "Cliente", fallbackKey: "customer_id", fallback: "Cliente non disponibile" },
    { key: "service_name", label: "Servizio", fallbackKey: "service_id", fallback: "Servizio non disponibile" },
    { key: "start_time", label: "Data e ora", fallbackKey: "appointment_id", fallback: "Orario non disponibile" },
    { key: "staff_name", label: "Staff", fallbackKey: "staff_id", fallback: "Staff non disponibile" },
    { key: "origin", label: "Origine" },
    { key: "reason", label: "Motivo" },
    { key: "slot_status", label: "Stato slot" },
  ],
  customer: [
    { key: "customer_name", label: "Cliente", fallbackKey: "customer_id", fallback: "Cliente non disponibile" },
    { key: "customer_source", label: "Origine" },
  ],
  staff: [
    { key: "staff_name", label: "Staff", fallbackKey: "staff_id", fallback: "Staff non disponibile" },
  ],
  recovery: [
    { key: "customer_name", label: "Cliente", fallbackKey: "customer_id", fallback: "Cliente non disponibile" },
    { key: "service_name", label: "Servizio", fallbackKey: "service_id", fallback: "Servizio non disponibile" },
    { key: "start_time", label: "Data e ora", fallbackKey: "appointment_id", fallback: "Orario non disponibile" },
    { key: "staff_name", label: "Staff", fallbackKey: "staff_id", fallback: "Staff non disponibile" },
    { key: "source_type", label: "Origine" },
    { key: "reason", label: "Motivo" },
    { key: "slot_status", label: "Stato slot" },
    { key: "reminder_type", label: "Tipo promemoria" },
    { key: "error_code", label: "Errore" },
  ],
  businessService: [
    { key: "service_name", label: "Servizio", fallbackKey: "service_id", fallback: "Servizio non disponibile" },
  ],
};

const isSafeMetadataKey = (key) => {
  const normalizedKey = key.toLowerCase();
  return !SENSITIVE_METADATA_PATTERNS.some((pattern) => normalizedKey.includes(pattern));
};

const hasMetadataValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";

const isTechnicalIdKey = (key) => key.toLowerCase().endsWith("_id");

const formatTechnicalId = (value) => {
  if (!hasMetadataValue(value)) {
    return "Non disponibile";
  }
  const normalized = String(value);
  if (normalized.length <= TECHNICAL_ID_MIN_LENGTH) {
    return normalized;
  }
  return `${normalized.slice(0, 6)}...${normalized.slice(-6)}`;
};

const formatMetadataValue = (key, value) => {
  if (!hasMetadataValue(value)) {
    return "Non disponibile";
  }
  if (/_at$/.test(key) || /_time$/.test(key)) {
    return formatDateTimeIT(value);
  }
  if (isTechnicalIdKey(key)) {
    return formatTechnicalId(value);
  }
  if (key === "performed_by" && value === "public_token") {
    return "link pubblico";
  }
  if (key === "performed_by" && value === "public_booking") {
    return "prenotazione pubblica";
  }
  if (key === "origin" && value === "online_booking") {
    return "prenotazione online";
  }
  if (key === "customer_source" && value === "online_booking") {
    return "prenotazione online";
  }
  if (key === "origin" && value === "manual") {
    return "manuale";
  }
  if (key === "reason" && value === "Manual cancellation") {
    return "Cancellazione manuale";
  }
  if (key === "slot_status") {
    const slotStatusLabels = {
      scheduled: "occupato",
      requested: "richiesto",
      completed: "completato",
      cancelled: "annullato",
      no_show: "no-show",
      released: "libero",
      recovered: "recuperato",
    };
    return slotStatusLabels[value] || String(value);
  }
  return String(value);
};

const buildMetadataPreview = (metadata, fields) => {
  return fields
    .filter(({ key, fallbackKey }) =>
      isSafeMetadataKey(key) && (hasMetadataValue(metadata[key]) || hasMetadataValue(metadata[fallbackKey]))
    )
    .map(({ key, label, fallback }) => ({
      label,
      value: hasMetadataValue(metadata[key]) ? formatMetadataValue(key, metadata[key]) : fallback,
      fullValue: hasMetadataValue(metadata[key]) ? String(metadata[key]) : fallback,
      isTechnicalId: isTechnicalIdKey(key),
    }));
};

export const getActivityMetadataPreview = (log) => {
  const metadata = log.metadata || {};
  const actionType = log.action_type || "";

  if (APPOINTMENT_ACTIONS.has(actionType)) {
    return buildMetadataPreview(metadata, METADATA_PREVIEW_FIELDS.appointment);
  }
  if (CUSTOMER_ACTIONS.has(actionType)) {
    return buildMetadataPreview(metadata, METADATA_PREVIEW_FIELDS.customer);
  }
  if (STAFF_ACTIONS.has(actionType)) {
    return buildMetadataPreview(metadata, METADATA_PREVIEW_FIELDS.staff);
  }
  if (RECOVERY_ACTIONS.has(actionType) || /^reminder_.*_sent$/.test(actionType)) {
    return buildMetadataPreview(metadata, METADATA_PREVIEW_FIELDS.recovery);
  }
  if (BUSINESS_SERVICE_ACTIONS.has(actionType)) {
    return buildMetadataPreview(metadata, METADATA_PREVIEW_FIELDS.businessService);
  }

  return [];
};

export const getTechnicalMetadata = (metadata = {}) => {
  return Object.entries(metadata)
    .filter(([key, value]) => isSafeMetadataKey(key) && hasMetadataValue(value))
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .map(([key, value]) => ({ key, value: String(value) }));
};

export const getActorBadge = (metadata = {}) => {
  const role = metadata.performed_by_role;
  const performedBy = metadata.performed_by;

  if (!role && !performedBy) {
    return {
      label: "Origine: non specificata",
      className: ACTOR_BADGE_CLASSES.missing,
    };
  }

  if (role === "system") {
    return {
      label: "Sistema",
      className: ACTOR_BADGE_CLASSES.system,
    };
  }

  const roleLabel = ACTOR_ROLE_LABELS[role] || role || "Non specificato";
  const actorLabel =
    metadata.performed_by_name ||
    (performedBy === "public_token"
      ? "link pubblico"
      : performedBy === "public_booking"
        ? "prenotazione pubblica"
        : performedBy);

  return {
    label: actorLabel ? `${roleLabel} · ${actorLabel}` : roleLabel,
    className: ACTOR_BADGE_CLASSES[role] || ACTOR_BADGE_CLASSES.missing,
  };
};

export const getActivityDestination = (log) => {
  const metadata = log?.metadata || {};
  if (metadata.cancellation_id && (
    metadata.recovery_attempt_id ||
    log?.action_type?.startsWith("recovery_") ||
    log?.action_type?.startsWith("cancellation_")
  )) {
    return {
      to: `/recovery?event=${encodeURIComponent(metadata.cancellation_id)}`,
      label: "Apri evento di recupero",
    };
  }
  const appointmentId = metadata.appointment_id || metadata.slot_appointment_id;
  if (appointmentId && metadata.start_time) {
    const dateKey = toBusinessDateKey(new Date(metadata.start_time));
    return {
      to: `/?date=${encodeURIComponent(dateKey)}&appointment=${encodeURIComponent(appointmentId)}`,
      label: "Apri in Agenda",
    };
  }
  return null;
};

const getReminderSentLabel = (actionType) => {
  const match = /^reminder_(.+)_sent$/.exec(actionType);
  if (!match) {
    return null;
  }
  return `Promemoria preparato (${match[1]})`;
};

const getReadableFallbackActionLabel = (actionType) => {
  if (!actionType) {
    return "Evento non specificato";
  }
  return `Evento tecnico non mappato`;
};

export const buildActivityParams = (filters) => {
  const params = {};
  const category = filters.category.trim();
  const search = filters.search.trim();
  const performedByRole = filters.performed_by_role.trim();
  const limit = filters.limit.trim();

  if (category) {
    params.category = category;
  }
  if (search) {
    params.search = search;
  }
  if (performedByRole) {
    params.performed_by_role = performedByRole;
  }
  if (filters.after) {
    params.after = `${filters.after}T00:00:00.000Z`;
  }
  if (filters.before) {
    const nextDay = new Date(`${filters.before}T00:00:00.000Z`);
    nextDay.setUTCDate(nextDay.getUTCDate() + 1);
    params.before = nextDay.toISOString();
  }
  if (limit) {
    params.limit = limit;
  }

  return params;
};

const getActivityLimit = (filters) => {
  const limit = Number.parseInt(filters.limit, 10);
  return Number.isFinite(limit) && limit > 0 ? limit : 20;
};

const hasActivityQuery = (filters) => Boolean(
  filters.category || filters.search.trim() || filters.performed_by_role || filters.after || filters.before
);

const appendUniqueLogs = (currentLogs, nextLogs) => {
  const seenIds = new Set(currentLogs.map((log) => log.id));
  const uniqueNextLogs = nextLogs.filter((log) => {
    if (seenIds.has(log.id)) {
      return false;
    }
    seenIds.add(log.id);
    return true;
  });

  return [...currentLogs, ...uniqueNextLogs];
};

export function ActivityPage() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({});
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  const [loadMoreError, setLoadMoreError] = useState("");

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/activity-logs/summary");
      setSummary(response.data.counts || {});
    } catch (loadError) {
      const message = "Caricamento riepilogo attività non riuscito.";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadData = useCallback(async (nextFilters) => {
    if (!hasActivityQuery(nextFilters)) {
      setLogs([]);
      setHasMore(false);
      setError("");
      return;
    }

    setLoading(true);
    setError("");
    setLoadMoreError("");
    try {
      const response = await api.get("/activity-logs", {
        params: buildActivityParams(nextFilters),
      });
      setLogs(response.data);
      setHasMore(response.data.length === getActivityLimit(nextFilters));
    } catch (loadError) {
      const message = "Caricamento registro attività non riuscito.";
      setError(message);
      toast.error(message);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters(filters);
    loadData(filters);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setLogs([]);
    setHasMore(false);
    setError("");
    setLoadMoreError("");
  };

  const selectCategory = (category) => {
    const nextCategory = filters.category === category ? "" : category;
    const nextFilters = { ...filters, category: nextCategory };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    loadData(nextFilters);
  };

  const loadMoreLogs = async () => {
    if (isLoadingMore || !hasMore || logs.length === 0) {
      return;
    }

    const before = logs[logs.length - 1].created_at;
    if (!before) {
      setHasMore(false);
      return;
    }

    setIsLoadingMore(true);
    setLoadMoreError("");
    try {
      const response = await api.get("/activity-logs", {
        params: {
          ...buildActivityParams(appliedFilters),
          before,
        },
      });
      setLogs((currentLogs) => appendUniqueLogs(currentLogs, response.data));
      setHasMore(response.data.length === getActivityLimit(appliedFilters));
    } catch (loadMoreErrorResponse) {
      const message = "Caricamento altre attività non riuscito.";
      setLoadMoreError(message);
      toast.error(message);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const formatTime = (value) => formatDateTimeIT(value);

  const getActionLabel = (actionType) => {
    return ACTION_LABELS[actionType] || getReminderSentLabel(actionType) || getReadableFallbackActionLabel(actionType);
  };

  const getMessageLabel = (message) => {
    if (!message) {
      return "Messaggio non disponibile.";
    }
    return MESSAGE_LABELS[message] || message;
  };

  const isQueryActive = hasActivityQuery(appliedFilters);

  return (
    <div className="space-y-6" data-testid="activity-page">
      <PageHeader
        title="Registro attività"
        subtitle="Consulta gli eventi registrati da FullBook e le operazioni eseguite."
        titleTestId="activity-title"
      />

      <section className="rounded-sm border border-slate-200 bg-white p-4 sm:p-6">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="activity-categories">
          {ACTIVITY_CATEGORIES.map((category) => {
            const isSelected = filters.category === category.key;
            return (
              <button
                key={category.key}
                type="button"
                onClick={() => selectCategory(category.key)}
                aria-expanded={isSelected}
                className={`flex min-h-16 items-center justify-between rounded-sm border px-3 py-2 text-left text-sm ${
                  isSelected
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                }`}
                data-testid={`activity-category-${category.key}`}
              >
                <span className="font-medium">{category.label}</span>
                <span className={isSelected ? "text-slate-200" : "text-slate-500"}>
                  {summary[category.key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        <form className="mt-4 grid gap-3 md:grid-cols-5" onSubmit={applyFilters} data-testid="activity-filters">
          <input
            name="search"
            aria-label="Cerca nel registro"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Cerca evento, cliente, servizio o staff"
            className="border border-slate-200 rounded-sm h-10 px-3 text-sm md:col-span-2"
            data-testid="activity-filter-search"
          />
          <select
            name="performed_by_role"
            aria-label="Origine o autore"
            value={filters.performed_by_role}
            onChange={handleFilterChange}
            className="border border-slate-200 rounded-sm h-10 px-3 text-sm bg-white"
            data-testid="activity-filter-role"
          >
            <option value="">Ogni origine</option>
            {roleOptions.map((role) => (
              <option key={role} value={role}>
                {ACTOR_ROLE_LABELS[role] || role}
              </option>
            ))}
          </select>
          <input
            type="date"
            name="after"
            aria-label="Data iniziale"
            value={filters.after}
            onChange={handleFilterChange}
            className="border border-slate-200 rounded-sm h-10 px-3 text-sm"
            data-testid="activity-filter-after"
          />
          <input
            type="date"
            name="before"
            aria-label="Data finale"
            value={filters.before}
            onChange={handleFilterChange}
            className="border border-slate-200 rounded-sm h-10 px-3 text-sm"
            data-testid="activity-filter-before"
          />
          <div className="md:col-span-5 flex flex-wrap gap-2">
          <button
            type="submit"
            disabled={loading || isLoadingMore}
            className="bg-slate-900 text-white rounded-sm h-10 px-4 text-sm font-medium disabled:opacity-60"
            data-testid="activity-apply-filters"
          >
            Applica filtri
          </button>
          <button
            type="button"
            onClick={clearFilters}
            disabled={loading || isLoadingMore}
            className="border border-slate-200 text-slate-700 rounded-sm h-10 px-4 text-sm font-medium disabled:opacity-60"
            data-testid="activity-clear-filters"
          >
            Pulisci
          </button>
          </div>
        </form>

      <div className="mt-4 space-y-3" data-testid="activity-list">
        {loading && (
          <p className="text-sm text-slate-500" data-testid="activity-loading">
            Caricamento attività...
          </p>
        )}
        {!loading && error && (
          <p className="text-sm text-red-600" data-testid="activity-error">
            {error}
          </p>
        )}
        {!loading && !error && !isQueryActive && (
          <p className="text-sm text-slate-500" data-testid="activity-guidance">
            Scegli una categoria oppure usa ricerca, periodo o origine per consultare gli eventi.
          </p>
        )}
        {!loading && !error && isQueryActive && logs.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="activity-empty">
            Nessuna attività trovata.
          </p>
        )}
        {!loading && !error && logs.map((log) => {
          const actorBadge = getActorBadge(log.metadata);
          const metadataPreview = getActivityMetadataPreview(log);
          const technicalMetadata = getTechnicalMetadata(log.metadata);
          const destination = getActivityDestination(log);

          return (
            <div key={log.id} className="border border-slate-200 rounded-sm p-3" data-testid={`activity-${log.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium" data-testid={`activity-title-${log.id}`}>
                    {getActionLabel(log.action_type)}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center rounded-sm border px-2 py-1 text-xs font-medium ${actorBadge.className}`}
                  data-testid={`activity-actor-${log.id}`}
                >
                  {actorBadge.label}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-700" data-testid={`activity-message-${log.id}`}>
                {getMessageLabel(log.message)}
              </p>
              {metadataPreview.length > 0 && (
                <div
                  className="mt-2 flex flex-wrap gap-2 text-xs"
                  data-testid={`activity-metadata-preview-${log.id}`}
                >
                  {metadataPreview.map((item) => (
                    <span
                      key={`${item.label}-${item.fullValue}`}
                      className="inline-flex max-w-full items-center gap-1 rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-slate-600"
                    >
                      <span className="font-medium text-slate-700">{item.label}:</span>
                      <span className="break-all">{item.value}</span>
                    </span>
                  ))}
                </div>
              )}
              {destination && (
                <Link
                  to={destination.to}
                  className="mt-3 inline-flex rounded-sm border border-slate-300 px-3 py-2 text-xs font-medium text-slate-800"
                  data-testid={`activity-open-${log.id}`}
                >
                  {destination.label}
                </Link>
              )}
              <details className="mt-2 text-xs text-slate-500">
                <summary className="cursor-pointer">Dettagli tecnici</summary>
                <div className="mt-2 space-y-1">
                  <p className="break-all">Tipo evento: {log.action_type}</p>
                  <p className="break-all">ID log: {log.id}</p>
                  {technicalMetadata.map((item) => (
                    <p key={`technical-${item.key}`} className="break-all">
                      {item.key}: {item.value}
                    </p>
                  ))}
                </div>
              </details>
              <p className="text-xs text-slate-400" data-testid={`activity-time-${log.id}`}>
                {formatTime(log.created_at)}
              </p>
            </div>
          );
        })}
        {!loading && !error && loadMoreError && (
          <p className="text-sm text-red-600" data-testid="activity-load-more-error">
            {loadMoreError}
          </p>
        )}
        {!loading && !error && logs.length > 0 && hasMore && (
          <button
            type="button"
            onClick={loadMoreLogs}
            disabled={isLoadingMore}
            className="border border-slate-200 text-slate-700 rounded-sm h-10 px-4 text-sm font-medium disabled:opacity-60"
            data-testid="activity-load-more"
          >
            {isLoadingMore ? "Caricamento..." : "Carica altro"}
          </button>
        )}
        {!loading && !error && logs.length > 0 && !hasMore && (
          <p className="text-sm text-slate-400" data-testid="activity-no-more">
            Nessun altro risultato
          </p>
        )}
        </div>
      </section>
    </div>
  );
}



